import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { defineMessages, FormattedMessage } from 'react-intl';
import axios from 'axios';

import { Modal } from 'ui-components/components/modal';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { Button } from 'ui-components/components/button';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';

import DestinationService from './destination-service';
import SelectableService from './selectable-service';
import ModalPortal from '../../../../components/modal-portal';
import Global from '../../../../store/global-selectors';

import style from './service-visibility-modal.module.less';

const DOMESTIC = 'domestic';
const INTERNATIONAL = 'international';
const destinations = [DOMESTIC, INTERNATIONAL];
const SELECT = 'select';
const DESELECT = 'deselect';

const messages = defineMessages({
  header: {
    defaultMessage: 'Shipping Options',
    id: 'm9gtXk',
    description: 'src.pages.settings.providers.service-visibility-modal.serviceVisibility',
  },
  description: {
    defaultMessage: 'Selected services will appear in the Services menu when you configure your shipping options.',
    id: 'UaKXWu',
    description: 'src.pages.settings.providers.service-visibility-modal.heading',
  },
  [DOMESTIC]: {
    defaultMessage: 'Domestic',
    id: 'x/eepc',
    description: 'src.pages.settings.providers.service-visibility-modal.domestic',
  },
  [INTERNATIONAL]: {
    defaultMessage: 'International',
    id: 'NZp00+',
    description: 'src.pages.settings.providers.service-visibility-modal.international',
  },
  [SELECT]: {
    defaultMessage: 'Select All',
    id: '9a/+sH',
    description: 'src.pages.settings.providers.service-visibility-modal.select-all',
  },
  [DESELECT]: {
    defaultMessage: 'Deselect All',
    id: 'CR2RM+',
    description: 'src.pages.settings.providers.service-visibility-modal.deselect-all',
  },
  cancel: {
    defaultMessage: 'Cancel',
    id: 'drkUeA',
    description: 'src.pages.settings.providers.service-visibility-modal.cancel',
  },
  save: {
    defaultMessage: 'Save',
    id: 'ctKcuQ',
    description: 'src.pages.settings.providers.service-visibility-modal.save',
  },
});

const ServiceVisibilityModal = ({ labelProviderId = '', sellerLabelProviderId = '', onSave, onClose }) => {
  const services = useSelector(Global.selectSellerServices);
  const [stateServices, setStateServices] = useAsyncSafeState({});
  const [loading, setLoading] = useAsyncSafeState(false);
  const [errorMessage, setErrorMessage] = useAsyncSafeState();
  const showSelectAll = !!Object.values(stateServices).find((value) => value.some((s) => s.isHidden));

  useEffect(() => {
    const filteredServices = services
      .filter((service) => service.sellerLabelProviderId === sellerLabelProviderId)
      .reduce((results, service) => {
        const destination = service.features.supportsInternationalShipping ? INTERNATIONAL : DOMESTIC;

        // DOMESTIC should always be first
        if (destination === DOMESTIC && !results[destination]) {
          return {
            [destination]: [service],
            ...results,
          };
        }

        return {
          ...results,
          [destination]: results[destination] ? [...results[destination], service] : [service],
        };
      }, {});
    setStateServices(filteredServices);
  }, [sellerLabelProviderId, services, setStateServices]);

  const handleToggleAll = () => {
    const newStateServices = destinations.reduce((results, destination) => {
      const value = stateServices[destination] || [];
      const newValue = value.map((service) => ({
        ...service,
        isHidden: !showSelectAll,
      }));
      return {
        ...results,
        [destination]: newValue,
      };
    }, {});

    setStateServices(newStateServices);
  };

  const handleOnSave = () => {
    setLoading(true);
    const combineStateServices = Object.values(stateServices).reduce((results, value) => [...results, ...value], []);
    axios
      .post('/service/toggleAvailability', {
        providerId: labelProviderId,
        serviceAvailabilities: combineStateServices.map((service) => ({
          serviceId: service.serviceId,
          isHidden: service.isHidden,
        })),
      })
      .then(() => onSave(combineStateServices))
      .catch((error) => {
        setErrorMessage(error.message);
        setLoading(false);
      });
  };

  return (
    <ModalPortal containerId='service-visibility-modal' onClose={onClose}>
      <Modal className={style['service-visibility-modal']}>
        <ModalHeader className={style['service-visibility-modal__header']}>
          <FormattedMessage {...messages.header} />
        </ModalHeader>
        <ModalBody className={style['service-visibility-modal__body']}>
          {loading && <LoadingIndicator />}
          {errorMessage && <div className={style['service-visibility-modal__error-message']}>{errorMessage}</div>}
          <div className={style['service-visibility-modal__body-content']}>
            <FormattedMessage {...messages.description} />
          </div>
          <div className={style['service-visibility-modal__service-list']}>
            {destinations.map((destination) => {
              const value = stateServices[destination] || [];
              return (
                <Fragment key={destination}>
                  <DestinationService>
                    <FormattedMessage {...messages[destination]} />
                  </DestinationService>
                  {value.map((service, index) => (
                    <SelectableService
                      key={service.serviceId}
                      checked={!service.isHidden}
                      name={service.name}
                      onCheck={() => {
                        const newValue = [...value];
                        newValue[index] = {
                          ...service,
                          isHidden: !service.isHidden,
                        };
                        setStateServices({
                          ...stateServices,
                          [destination]: newValue,
                        });
                      }}
                    />
                  ))}
                </Fragment>
              );
            })}
          </div>
        </ModalBody>
        <ModalFooter className={style['service-visibility-modal__footer']}>
          <Button onClick={handleToggleAll} theme='link'>
            <FormattedMessage {...messages[showSelectAll ? SELECT : DESELECT]} />
          </Button>
          <div className={style['service-visibility-modal__actions']}>
            <Button onClick={onClose} theme='tertiary'>
              <FormattedMessage {...messages.cancel} />
            </Button>
            <Button disabled={errorMessage} onClick={handleOnSave} theme='primary'>
              <FormattedMessage {...messages.save} />
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    </ModalPortal>
  );
};

ServiceVisibilityModal.propTypes = {
  labelProviderId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  sellerLabelProviderId: PropTypes.string,
};

export default ServiceVisibilityModal;
